import { defineStore } from "pinia"
import { computed, reactive, ref, watch } from "vue"
import {
  getDefaultState,
  IntStateData,
  type IntProviderState
} from "@/modules/provide/create-offer/interfaces/IProviderState"
import providerService from "@/modules/provide/create-offer/services/providerService"

export const useProviderStore = defineStore("providerStore", () => {
  const state = reactive<IntProviderState>(getDefaultState())
  const selectedUsecase = ref<any>({})
  const modelsForSelectedUsecase = ref<any>([])
  const showWizardGettingStartedPopup = ref<boolean>(false)
  const allActiveUseCases = computed(() => state.allActiveUseCases)
  const activeUseCaseIds = computed<string[]>(
    () => allActiveUseCases.value?.data?.map((item: { id: string }) => item.id) ?? []
  )
  const useCases = computed(() => state.useCases)
  const models = computed(() => state.models)
  const assetProvisionForm = computed(() => state.assetProvisionForm)
  const digitalTwinTypes = computed(() => state.digitalTwinTypes)
  const offerCategories = computed(() => state.offerCategories)
  const getModelsForSelectedUsecase = computed(() => modelsForSelectedUsecase.value)
  const isShowWizardGettingStartedPopup = computed(() => showWizardGettingStartedPopup.value)
  const aspectsCountData = computed(() => state.aspectsCountData)
  const connectorCountData = computed(() => state.connectorCountData)

  // it watches changes in selected Usecase (provider - Step1)
  // accordingly filters the models list (modelsForSelectedUsecase) for the same component
  watch(selectedUsecase, (newUsecase: any) => {
    const shellTypes = newUsecase?.shellTypes ?? {}

    const shellTypesNames = Object.keys(shellTypes)

    const modelsIds = shellTypesNames
      .map((name: string) => Object.keys(shellTypes[name]?.conversions ?? {}))
      .flat()

    modelsForSelectedUsecase.value = (models.value?.data ?? []).filter((model: any) =>
      modelsIds.includes(model._id)
    )
  })

  const setSelectedUsecase = (data: any) => {
    selectedUsecase.value = data
  }

  function updateIsShowWizardGettingStartedPopup(value: boolean) {
    showWizardGettingStartedPopup.value = value || !showWizardGettingStartedPopup.value
  }

  const updateFormValues = (key: string, value: string | number) => {
    state.assetProvisionForm[key] = value
  }

  const setInitiateState = (payload: {
    key: keyof IntProviderState
    data: any
    selectionType: "single" | "multiselect"
  }): void => {
    if (payload.key === "offerCategories") {
      // reset other selection if create offer category changes
      state.useCases.selectedData = []
      state.digitalTwinTypes.selectedData = []
    }

    if (payload.key === "offerCategories" || payload.key === "useCases") {
      state.digitalTwinTypes.data = []
      state.digitalTwinTypes.selectedData = []
      state.models.data = []
      state.models.selectedData = []
    }

    if (typeof state[payload.key] === "object") {
      ;(state[payload.key] as unknown as IntStateData).selectedData = []
      if (Array.isArray(payload.data)) {
        ;(state[payload.key] as unknown as IntStateData).selectedData = payload.data
      } else {
        ;(state[payload.key] as unknown as IntStateData).selectedData?.push(payload.data)
      }
    }

    // populate digital twin types as per usecases
    if (payload.key === "useCases") {
      const _digitalTwinTypes: unknown[] = []

      state.useCases.selectedData?.forEach((item: any) => {
        Object.entries(item.shellTypes ?? {}).forEach(([key, value]: any) => {
          _digitalTwinTypes.push({ ...(value as object), _id: key })
        })
      })

      state.digitalTwinTypes.data = _digitalTwinTypes

      const _subModels: any[] = []

      state.digitalTwinTypes.data?.forEach((item: any) => {
        Object.entries(item.conversions ?? {}).forEach(([key, value]: any) => {
          _subModels.push({
            ...(value as object),
            _id: key,
            shellName: item.name
          })
        })
      })

      state.models.data = [..._subModels]
    }

    // populate sub models from digital twin types
    if (payload.key === "digitalTwinTypes") {
      const _subModels: any[] = []

      state.digitalTwinTypes.selectedData?.forEach((item: any) => {
        Object.entries(item.conversions ?? {}).forEach(([key, value]: any) => {
          _subModels.push({
            ...(value as object),
            _id: key,
            shellName: item.name
          })
        })
      })

      state.models.data = [..._subModels]
    }
  }

  const fetchUsecases = async () => {
    try {
      state.useCases.isLoading = true

      const { data } = await providerService.fetchUseCases()

      type UseCase = {
        name: string
        shellTypes?: Record<string, { conversions?: Record<string, any> }>
      }

      // List all usecase list
      state.allActiveUseCases.data =
        (data?.content as UseCase[])?.sort((a: any, b: any) => a.name.localeCompare(b.name)) || []

      // Filter use cases
      state.useCases.data =
        (data?.content as UseCase[])
          ?.filter((item) =>
            Object.values(item.shellTypes ?? {}).some(
              (st) => Object.keys(st.conversions ?? {}).length > 0
            )
          )
          .sort((a, b) => a.name.localeCompare(b.name)) || []

      // populate submodels
      const _allModels: any[] = []

      state.useCases.data.forEach((item: any) => {
        Object.keys(item.shellTypes ?? {}).forEach((skey) => {
          Object.entries(item.shellTypes[skey]?.conversions ?? {}).forEach(([key, value]: any) => {
            _allModels.push({
              ...(value as object),
              _id: key,
              shellName: item.shellTypes[skey].name
            })
          })
        })
      })

      state.models.data = [..._allModels]
    } catch (err) {
      console.error("Failed to fetch use cases:", err)

      state.allActiveUseCases = {
        data: [],
        isLoading: false,
        selectedData: null
      }

      state.useCases = {
        data: [],
        selectedData: [],
        isLoading: false
      }

      state.models = {
        data: [],
        selectedData: [],
        isLoading: false
      }
    } finally {
      state.useCases.isLoading = false
    }
  }

  return {
    useCases,
    allActiveUseCases,
    activeUseCaseIds,
    models,
    digitalTwinTypes,
    setInitiateState,
    offerCategories,
    isShowWizardGettingStartedPopup,
    updateIsShowWizardGettingStartedPopup,
    assetProvisionForm,
    updateFormValues,
    fetchUsecases,
    setSelectedUsecase,
    getModelsForSelectedUsecase,
    aspectsCountData,
    connectorCountData
  }
})
